import React, { Fragment, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { ChevronDown } from "lucide-react";
import translations from "app/libs/i18n/translations.json";

const Faq = ({ type }) => {
  const [panelExpanded, setPanelExpanded] = useState("");
  const handleChange = (newPanel) => () => {
    setPanelExpanded(panelExpanded === newPanel ? "" : newPanel);
  };

  const getList = () => {
    const data = Object.keys(translations["en"]).filter((item) =>
      item.startsWith(`faq.${type}`)
    );
    let result = {};

    data.forEach((p) =>
      p.split(".").reduce((o, k) => (o[k] = o[k] || {}), result)
    );

    return Object.values(result.faq[type]);
  };
  const optionsList = getList();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "3rem", md: "5rem" },
        px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        bgcolor: "hsl(230 100% 99%)",
      }}
    >
      <Container maxWidth="lg" sx={{ maxWidth: "72rem" }}>
        <Box
          component="span"
          sx={{
            display: "block",
            textAlign: "center",
            fontSize: "0.875rem",
            fontWeight: 700,
            color: "hsl(153 68% 25%)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            mb: "0.5rem",
          }}
        >
          FAQ
        </Box>

        {/* Title */}
        <Box
          component="h2"
          sx={{
            m: 0,
            mb: { xs: "2rem", md: "3rem" },
            textAlign: "center",
            fontSize: { xs: "1.875rem", md: "2.25rem" },
            fontWeight: 900,
            color: "hsl(178 93% 10%)",
            letterSpacing: "-0.02em",
            fontFamily: "'Segoe UI', 'Work Sans', sans-serif",
            lineHeight: 1.15,
          }}
        >
          <FormattedMessage id="faq.title" />
        </Box>

        {/* Sections */}
        <Box sx={{ maxWidth: "48rem", mx: "auto" }}>
          {optionsList.map(({ ...section }, sectionIndex) => (
            <Fragment key={sectionIndex}>
              {/* Section title */}
              <Box
                component="h3"
                sx={{
                  m: 0,
                  mt: sectionIndex > 0 ? "2.5rem" : 0,
                  mb: "1rem",
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  fontWeight: 700,
                  color: "hsl(153 68% 25%)",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  lineHeight: 1.3,
                }}
              >
                <FormattedMessage
                  id={`faq.${type}.section${sectionIndex + 1}.title`}
                  defaultMessage={
                    translations["en"][
                      `faq.${type}.section${sectionIndex + 1}.title`
                    ]
                  }
                />
              </Box>

              {/* Questions */}
              {Object.values(section)
                .filter((val) => val["question"] != null)
                .map((val, index) => {
                  const panelId = `s${sectionIndex}q${index}`;
                  const isExpanded = panelExpanded === panelId;

                  return (
                    <Accordion
                      key={index}
                      expanded={isExpanded}
                      onChange={handleChange(panelId)}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: "0.75rem",
                        borderRadius: "0.75rem !important",
                        border: "1px solid",
                        borderColor: isExpanded
                          ? "hsl(153 68% 25% / 0.3)"
                          : "hsl(220 13% 91%)",
                        bgcolor: "hsl(0 0% 100%)",
                        transition: "all 200ms ease",
                        "&:before": { display: "none" },
                        "&:hover": {
                          borderColor: "hsl(153 68% 25% / 0.3)",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ChevronDown
                            size={18}
                            style={{
                              color: isExpanded
                                ? "hsl(153, 68%, 25%)"
                                : "hsl(220, 10%, 46%)",
                              transition: "transform 200ms ease",
                            }}
                          />
                        }
                        sx={{
                          px: "1.25rem",
                          py: "0.25rem",
                          minHeight: "auto",
                          "& .MuiAccordionSummary-content": {
                            my: "0.75rem",
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.9375rem",
                            fontWeight: 600,
                            fontFamily:
                              "Segoe UI Semibold, system-ui, sans-serif",
                            color: isExpanded
                              ? "hsl(153 68% 25%)"
                              : "hsl(178 93% 10%)",
                            lineHeight: 1.5,
                            transition: "color 200ms ease",
                          }}
                        >
                          <FormattedMessage
                            id={`faq.${type}.section${sectionIndex + 1}.q${
                              index + 1
                            }.question`}
                            defaultMessage={
                              translations["en"][
                                `faq.${type}.section${sectionIndex + 1}.q${
                                  index + 1
                                }.question`
                              ]
                            }
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          px: "1.25rem",
                          pt: 0,
                          pb: "1.25rem",
                        }}
                      >
                        <Box
                          component="p"
                          sx={{
                            m: 0,
                            fontSize: "0.875rem",
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                            color: "hsl(220 10% 46%)",
                            lineHeight: 1.7,
                            "& a": {
                              color: "hsl(153 68% 25%)",
                              fontWeight: 600,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            },
                          }}
                        >
                          <FormattedMessage
                            id={`faq.${type}.section${sectionIndex + 1}.q${
                              index + 1
                            }.answer`}
                            defaultMessage={
                              translations["en"][
                                `faq.${type}.section${sectionIndex + 1}.q${
                                  index + 1
                                }.answer`
                              ]
                            }
                            values={{
                              a: (link) => <a href="/contact-us">{link}</a>,
                              b: (bold) => (
                                <span style={{ fontWeight: 600 }}>{bold}</span>
                              ),
                            }}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
            </Fragment>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Faq;
